import { Check, ChevronDown, Eye, PauseCircle, Zap } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useFlowActions, useFlowByDocumentId } from '@/hooks/use-flows'
import { flowPosture, type FlowPosture } from '@/lib/api/flows'
import { QUIET_OVERFLOW_MENU_CONTENT_CLASS } from '@/lib/menu-chrome'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

const OPTIONS: { id: FlowPosture; label: string; icon: typeof Zap }[] = [
  { id: 'disabled', label: 'Disabled', icon: PauseCircle },
  { id: 'shadow', label: 'Shadow', icon: Eye },
  { id: 'live', label: 'Live', icon: Zap },
]

export function FlowRuntimeControl({ flowId }: { flowId: string }) {
  const { flow, refetch } = useFlowByDocumentId(flowId)
  const { setPosture, loading } = useFlowActions()
  const posture: FlowPosture = flow ? flowPosture(flow) : 'disabled'
  const current = OPTIONS.find((option) => option.id === posture) ?? OPTIONS[0]
  const Icon = current.icon

  const choose = async (mode: FlowPosture) => {
    if (!flow || mode === posture) return
    try {
      await setPosture(flow.id, mode)
      await refetch()
      toast.success(mode === 'disabled' ? 'Flow disabled' : mode === 'shadow' ? 'Shadow' : 'Live')
    } catch {
      toast.error('Could not change flow posture')
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 gap-1.5 px-2 text-xs"
          disabled={!flow || loading}
          data-testid={testIds.flowDocumentRenderer.posture}
        >
          <Icon className="h-3.5 w-3.5" aria-hidden />
          {current.label}
          <ChevronDown className="h-3 w-3 text-muted-foreground" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={QUIET_OVERFLOW_MENU_CONTENT_CLASS}>
        {OPTIONS.map((option) => {
          const OptionIcon = option.icon
          return (
            <DropdownMenuItem key={option.id} onClick={() => { void choose(option.id) }}>
              <OptionIcon className="mr-2 h-4 w-4" />
              {option.label}
              <Check className={cn('ml-auto h-4 w-4', posture === option.id ? 'opacity-100' : 'opacity-0')} />
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
